import type {
  CalendarEntry,
  CalendarPerson,
  CalendarSettings,
  CalendarSupplier,
  ChantierCalendar,
} from "@/lib/chantier-calendar/types";

// How a chantier's calendar travels. The server keeps a revision per chantier
// (the newest id of its change history, see sync.server.ts); the browser keeps
// the last one it saw and asks for what came after. Shared by both sides; no
// server code lives here.

/** What the browser keeps: the calendar as the screens read it, and the revision it is at. */
export type ChantierCalendarData = ChantierCalendar & { preview: boolean; revision: number };

/** An entry on the wire: the person who last changed it is named once, in `people`. */
export type WireEntry = Omit<CalendarEntry, "updatedBy"> & { updatedById: string | null };

interface SyncHeader {
  /** Newest change the answer includes (it may carry later ones, never fewer). */
  revision: number;
  project: ChantierCalendar["project"];
  preview: boolean;
}

export type CalendarSync =
  /** Everything: a first load, or a revision the server cannot build a difference from. */
  | (SyncHeader & {
      kind: "full";
      settings: CalendarSettings;
      suppliers: CalendarSupplier[];
      people: CalendarPerson[];
      entries: WireEntry[];
    })
  /** Nothing changed since the revision asked for. */
  | (SyncHeader & { kind: "unchanged" })
  /** What changed since the revision asked for: what to put in, and what to take out. */
  | (SyncHeader & {
      kind: "delta";
      /** Present only when the settings changed. */
      settings: CalendarSettings | null;
      suppliers: { upserted: CalendarSupplier[]; removed: string[] };
      entries: { upserted: WireEntry[]; removed: string[] };
      people: CalendarPerson[];
    });

/** More changed records than this and the server sends everything: a difference that big is no saving. */
export const MAX_DELTA_RECORDS = 300;

export interface SlimEntries {
  entries: WireEntry[];
  people: CalendarPerson[];
}

/** Names each person once; the entries only point at them. */
export function slimEntries(entries: readonly CalendarEntry[]): SlimEntries {
  const people = new Map<string, CalendarPerson>();

  const slim = entries.map(({ updatedBy, ...entry }) => {
    if (updatedBy) people.set(updatedBy.id, updatedBy);

    return { ...entry, updatedById: updatedBy?.id ?? null };
  });

  return { entries: slim, people: [...people.values()] };
}

/** The reverse of `slimEntries`: every entry gets its person back, the same object for the same person. */
export function hydrateEntries(
  entries: readonly WireEntry[],
  people: readonly CalendarPerson[],
): CalendarEntry[] {
  const byId = new Map(people.map((person) => [person.id, person]));

  return entries.map(({ updatedById, ...entry }) => ({
    ...entry,
    updatedBy: (updatedById && byId.get(updatedById)) || null,
  }));
}

const byName = (a: CalendarSupplier, b: CalendarSupplier) => a.name.localeCompare(b.name, "fr");

/** Puts `upserted` in the list (replacing by id, appending what is new) and drops `removed`. */
function merge<T extends { id: string }>(
  current: readonly T[],
  upserted: readonly T[],
  removed: readonly string[],
): T[] {
  const incoming = new Map(upserted.map((item) => [item.id, item]));
  const gone = new Set(removed);

  const kept = current.flatMap((item) =>
    gone.has(item.id) ? [] : [incoming.get(item.id) ?? item],
  );

  const known = new Set(current.map((item) => item.id));

  for (const item of upserted) if (!known.has(item.id)) kept.push(item);

  return kept;
}

/**
 * The calendar the browser should hold after an answer. An unchanged answer
 * hands back the very same object, so nothing downstream renders again; a
 * difference only replaces what it names, so every other entry keeps its
 * identity.
 */
export function reconcile(
  cached: ChantierCalendarData | undefined,
  sync: CalendarSync,
): ChantierCalendarData {
  if (sync.kind === "full") {
    return {
      project: sync.project,
      preview: sync.preview,
      revision: sync.revision,
      settings: sync.settings,
      suppliers: sync.suppliers,
      entries: hydrateEntries(sync.entries, sync.people),
    };
  }

  if (!cached) throw new Error("Le calendrier n'est pas chargé : rechargez la page.");

  const header = { project: sync.project, preview: sync.preview, revision: sync.revision };

  if (sync.kind === "unchanged") {
    const same =
      cached.revision === header.revision &&
      cached.preview === header.preview &&
      cached.project.name === header.project.name &&
      cached.project.projectNumber === header.project.projectNumber &&
      cached.project.location === header.project.location &&
      cached.project.calendarSetupCompleted === header.project.calendarSetupCompleted &&
      cached.project.canManage === header.project.canManage &&
      cached.project.canEdit === header.project.canEdit &&
      cached.project.calendarReadOnly === header.project.calendarReadOnly;

    return same ? cached : { ...cached, ...header };
  }

  const upserted = hydrateEntries(sync.entries.upserted, sync.people);
  const entries = merge(cached.entries, upserted, sync.entries.removed);
  // Array#sort is stable: entries that share a day keep the order they had.
  entries.sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));
  const suppliers = merge(cached.suppliers, sync.suppliers.upserted, sync.suppliers.removed);
  suppliers.sort(byName);

  return { ...cached, ...header, settings: sync.settings ?? cached.settings, suppliers, entries };
}

/**
 * Whether the calendar on screen may have missed changes, given the revision the
 * server reports. Nothing loaded yet is not behind (its own read is on the way);
 * a revision the server could not read counts as behind, to be safe.
 */
export function isBehind(held: number | undefined, server: number | null): boolean {
  if (held === undefined) return false;

  return server === null || server !== held;
}
